/**
 * "Join the interest list" on a vendor page (docs/MarketplaceDesign.md
 * section 3.4, docs/Decisions.md D68): the member picks a service, may add a
 * phone number and a note, and consents to share their contact details with
 * this vendor. Nothing is shared without the consent box (CR-34). A `ref`
 * in the page address records the member who recommended the vendor.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as marketApi from '../api/marketplace';
import { useToast } from './toast';
import { Field, SelectField, TextareaField } from './ui';
import { fieldErrorsFrom } from '../lib/formErrors';
import { cn, ui } from '../lib/ui';

export default function JoinInterestList({ vendor, categories, onJoined }) {
  const toast = useToast();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    serviceName: 'General interest',
    categoryId: categories.length === 1 ? categories[0].categoryId : '',
    phone: '',
    note: '',
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [joined, setJoined] = useState(false);
  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await marketApi.joinList(vendor.vendorId, {
        serviceName: form.serviceName,
        categoryId: form.categoryId || undefined,
        phone: form.phone.trim() || undefined,
        note: form.note.trim() || undefined,
        ref: params.get('ref') || undefined,
      });
      toast.success(`You are on ${vendor.businessName}'s interest list`);
      setJoined(true);
      setOpen(false);
      onJoined?.();
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      toast.error(err.message || 'You were not added');
    } finally {
      setSending(false);
    }
  }

  if (joined) {
    return (
      <p className={cn(ui.alertSuccess, 'mb-6')}>
        You joined the interest list. {vendor.businessName} will contact you; you can leave the list
        any time from the marketplace page.
      </p>
    );
  }
  if (!open) {
    return (
      <button
        type="button"
        className={cn(ui.btnPrimary, 'mb-6 w-full')}
        onClick={() => setOpen(true)}
      >
        Join the interest list
      </button>
    );
  }
  return (
    <form onSubmit={submit} className={cn(ui.cardFlat, 'mb-6')} noValidate>
      <h2 className="mt-0 text-title-s">Join the interest list</h2>
      <SelectField
        label="What are you interested in?"
        value={form.serviceName}
        onChange={e => set('serviceName', e.target.value)}
        error={errors.serviceName}
      >
        <option value="General interest">General interest</option>
        {(vendor.services || []).map(s => (
          <option key={s.name} value={s.name}>
            {s.name}
          </option>
        ))}
      </SelectField>
      {categories.length > 1 && (
        <SelectField
          label="Kind of service"
          value={form.categoryId}
          onChange={e => set('categoryId', e.target.value)}
          error={errors.categoryId}
        >
          <option value="">Choose one</option>
          {categories.map(c => (
            <option key={c.categoryId} value={c.categoryId}>
              {c.name}
            </option>
          ))}
        </SelectField>
      )}
      <Field
        label="Phone (optional)"
        type="tel"
        value={form.phone}
        onChange={e => set('phone', e.target.value)}
        error={errors.phone}
      />
      <TextareaField
        label="Anything the vendor should know (optional)"
        rows={2}
        maxLength={500}
        value={form.note}
        onChange={e => set('note', e.target.value)}
        error={errors.note}
      />
      <label className="mb-1 flex items-start gap-2 text-body-s">
        <input
          type="checkbox"
          className={cn(ui.checkbox, 'mt-1')}
          checked={form.consent}
          onChange={e => set('consent', e.target.checked)}
        />
        <span>
          Share my name, email and phone with {vendor.businessName} so they can contact me about
          this service.
        </span>
      </label>
      {errors.consent && <p className={ui.fieldError}>{errors.consent}</p>}
      <div className={cn(ui.row, 'mt-3')}>
        <button type="submit" className={ui.btnPrimary} disabled={sending || !form.consent}>
          {sending ? 'Joining...' : 'Join'}
        </button>
        <button type="button" className={ui.btnGhost} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
